import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  ChevronDown,
  ChevronRight,
  Rss,
  MessageCircle,
  Check,
  X,
  HelpCircle,
  ArrowUpRight,
  MoreHorizontal,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientFeed,
  SimulateFeedContent,
  SimulateGetFeedContentInputInterface,
  ClientBelief,
  GetBeliefsInputInterface,
  GetBeliefsResponseInterface,
  SimulateContentData,
  GetSupportingBeliefCountFromBeliefsInputInterface,
  BeliefSupportCount,
  ClientUserInterface,
} from '../../Admin-ClientLibrary';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import RecursiveBeliefModal from '../../components/RecursiveBeliefModal';
import SwitchContentModal from '../../components/SwitchContentModal';
import './FeedSimulation.css';
import { getApiUrl } from '../../config/api';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const FeedSimulation: React.FC = () => {
  const { feedId } = useParams<{ feedId: string }>();
  const navigate = useNavigate();

  const [feed, setFeed] = useState<ClientFeed | null>(null);
  const [feedContent, setFeedContent] = useState<SimulateFeedContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBeliefs, setExpandedBeliefs] = useState<Set<number>>(new Set());
  const [supportingBeliefs, setSupportingBeliefs] = useState<Record<number, ClientBelief[]>>({});
  const [loadingSupportingBeliefs, setLoadingSupportingBeliefs] = useState<Set<number>>(new Set());
  const [supportingBeliefCounts, setSupportingBeliefCounts] = useState<Record<number, number>>({});
  const [loadingSupportingBeliefCounts, setLoadingSupportingBeliefCounts] =
    useState<boolean>(false);
  const [nestedSupportingBeliefCounts, setNestedSupportingBeliefCounts] = useState<
    Record<number, number>
  >({});
  const [loadingNestedSupportingBeliefCounts, setLoadingNestedSupportingBeliefCounts] = useState<
    Set<number>
  >(new Set());

  // Top User Beliefs state
  const [powerUserBeliefs, setPowerUserBeliefs] = useState<Record<number, ClientBelief[]>>({});
  const [powerUserBeliefCounts, setPowerUserBeliefCounts] = useState<Record<number, number>>({});
  const [expandedPowerUserBeliefs, setExpandedPowerUserBeliefs] = useState<Set<number>>(new Set());
  const [loadingPowerUserBeliefs, setLoadingPowerUserBeliefs] = useState<Set<number>>(new Set());

  // Top Users state (fetched once at page level)
  const [powerUsers, setPowerUsers] = useState<ClientUserInterface[]>([]);
  const [loadingPowerUsers, setLoadingPowerUsers] = useState<boolean>(false);

  // Modal state for recursive belief exploration
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedBelief, setSelectedBelief] = useState<ClientBelief | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState<any>(null);

  // Switch content modal state
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState<boolean>(false);
  const [contentToSwitch, setContentToSwitch] = useState<SimulateFeedContent | null>(null);

  // Action menu state
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);

  // Fetch feed details
  const fetchFeed = async (): Promise<void> => {
    if (!feedId) return;

    try {
      const response = await adminApi.getFeed({ feedId: parseInt(feedId) });

      if (response.success && response.feed) {
        setFeed(response.feed);
      } else {
        setError(response.message || 'Feed not found');
      }
    } catch (err: any) {
      console.error('Error fetching feed:', err);
      setError('Failed to fetch feed details');
    }
  };

  // Fetch top users (users 11-110) once at page level
  const fetchPowerUsers = async (): Promise<void> => {
    setLoadingPowerUsers(true);
    try {
      const response = await adminApi.getUsers({
        afterId: 10, // Get users after ID 10 (so starting from 11)
        limit: 100, // Get 100 users (11-110)
        orderBy: 'id',
        orderDirection: 'ASC',
      });

      if (response.success) {
        console.log('Successfully fetched top users:', response.users.length, 'users found');
        setPowerUsers(response.users);
      } else {
        console.error('Failed to fetch top users:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching top users:', err);
    } finally {
      setLoadingPowerUsers(false);
    }
  };

  // Fetch feed content for simulation
  const fetchFeedContent = async (): Promise<void> => {
    if (!feedId) return;

    try {
      const apiParams: SimulateGetFeedContentInputInterface = {
        feedId: parseInt(feedId),
        limit: 20,
      };

      console.log('Fetching simulated feed content with params:', apiParams);

      const response = await adminApi.simulateGetFeedContent(apiParams);

      if (response.success) {
        setFeedContent(response.feedContent);
        // Fetch supporting belief counts after feed content is loaded
        await fetchSupportingBeliefCounts(response.feedContent);
      } else {
        setError(response.message || 'Failed to fetch feed content');
        setFeedContent([]);
      }
    } catch (err: any) {
      console.error('Error fetching simulated feed content:', err);
      setError('Network error occurred');
      setFeedContent([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch supporting belief counts for all beliefs in the feed
  const fetchSupportingBeliefCounts = async (content: SimulateFeedContent[]): Promise<void> => {
    // Extract belief IDs from the feed content
    const beliefIds: number[] = [];

    content.forEach((item) => {
      if (item.content.type === 'belief' && item.content.content?.id) {
        beliefIds.push(item.content.content.id);
      }
    });

    if (beliefIds.length === 0) {
      console.log('No beliefs found in feed content');
      return;
    }

    setLoadingSupportingBeliefCounts(true);

    try {
      const apiParams: GetSupportingBeliefCountFromBeliefsInputInterface = {
        beliefIds,
      };

      console.log('Fetching supporting belief counts for beliefs:', beliefIds);

      const response = await adminApi.getSupportingBeliefCountFromBeliefs(apiParams);

      if (response.success) {
        // Convert array to object for easy lookup
        const countsMap: Record<number, number> = {};
        response.supportingBeliefCounts.forEach((count) => {
          countsMap[count.beliefId] = count.supportingBeliefCount;
        });

        setSupportingBeliefCounts(countsMap);
        console.log('Successfully fetched supporting belief counts:', countsMap);
      } else {
        console.error('Failed to fetch supporting belief counts:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching supporting belief counts:', err);
    } finally {
      setLoadingSupportingBeliefCounts(false);
    }
  };

  // Fetch supporting beliefs for a specific belief
  const fetchSupportingBeliefs = async (beliefId: number): Promise<void> => {
    if (supportingBeliefs[beliefId] || loadingSupportingBeliefs.has(beliefId)) {
      return;
    }

    setLoadingSupportingBeliefs((prev) => new Set(prev).add(beliefId));

    try {
      const apiParams: GetBeliefsInputInterface = {
        supportingBeliefsFor: beliefId,
        limit: 50,
      };

      console.log(
        'Fetching supporting beliefs for belief ID:',
        beliefId,
        'with params:',
        apiParams
      );

      const response = await adminApi.getBeliefs(apiParams);

      if (response.success) {
        console.log(
          'Successfully fetched supporting beliefs:',
          response.beliefs.length,
          'beliefs found'
        );
        setSupportingBeliefs((prev) => ({
          ...prev,
          [beliefId]: response.beliefs,
        }));

        // Fetch supporting belief counts for these supporting beliefs
        if (response.beliefs.length > 0) {
          await fetchNestedSupportingBeliefCounts(response.beliefs);
        }
      } else {
        console.log('Failed to fetch supporting beliefs:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching supporting beliefs:', err);
    } finally {
      setLoadingSupportingBeliefs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(beliefId);
        return newSet;
      });
    }
  };

  // Fetch supporting belief counts for supporting beliefs (nested level)
  const fetchNestedSupportingBeliefCounts = async (beliefs: ClientBelief[]): Promise<void> => {
    const beliefIds = beliefs.map((belief) => belief.id);

    if (beliefIds.length === 0) {
      return;
    }

    // Mark these beliefs as loading their counts
    setLoadingNestedSupportingBeliefCounts((prev) => {
      const newSet = new Set(prev);
      beliefIds.forEach((id) => newSet.add(id));
      return newSet;
    });

    try {
      const apiParams: GetSupportingBeliefCountFromBeliefsInputInterface = {
        beliefIds,
      };

      console.log('Fetching nested supporting belief counts for beliefs:', beliefIds);

      const response = await adminApi.getSupportingBeliefCountFromBeliefs(apiParams);

      if (response.success) {
        // Update nested supporting belief counts
        setNestedSupportingBeliefCounts((prev) => {
          const updated = { ...prev };
          response.supportingBeliefCounts.forEach((count) => {
            updated[count.beliefId] = count.supportingBeliefCount;
          });
          return updated;
        });
        console.log(
          'Successfully fetched nested supporting belief counts:',
          response.supportingBeliefCounts
        );
      } else {
        console.error('Failed to fetch nested supporting belief counts:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching nested supporting belief counts:', err);
    } finally {
      // Remove loading state for these beliefs
      setLoadingNestedSupportingBeliefCounts((prev) => {
        const newSet = new Set(prev);
        beliefIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  // Fetch top user beliefs for a specific thought
  const fetchPowerUserBeliefs = async (thoughtId: number): Promise<void> => {
    if (powerUserBeliefs[thoughtId] || loadingPowerUserBeliefs.has(thoughtId)) {
      return;
    }

    setLoadingPowerUserBeliefs((prev) => new Set(prev).add(thoughtId));

    try {
      const apiParams: GetBeliefsInputInterface = {
        thoughtId,
        goeUserId: 11, // Top users start from user ID 11
        loeUserId: 110, // Top users end at user ID 110
        limit: 100,
      };

      console.log(
        'Fetching top user beliefs for thought ID:',
        thoughtId,
        'with params:',
        apiParams
      );

      const response = await adminApi.getBeliefs(apiParams);

      if (response.success) {
        console.log(
          'Successfully fetched top user beliefs:',
          response.beliefs.length,
          'beliefs found'
        );
        setPowerUserBeliefs((prev) => ({
          ...prev,
          [thoughtId]: response.beliefs,
        }));

        // Fetch supporting belief counts for top user beliefs
        if (response.beliefs.length > 0) {
          await fetchPowerUserBeliefCounts(response.beliefs);
        }
      } else {
        console.error('Failed to fetch top user beliefs:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching top user beliefs:', err);
    } finally {
      setLoadingPowerUserBeliefs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(thoughtId);
        return newSet;
      });
    }
  };

  // Fetch supporting belief counts for top user beliefs
  const fetchPowerUserBeliefCounts = async (beliefs: ClientBelief[]): Promise<void> => {
    const beliefIds = beliefs.map((belief) => belief.id);

    if (beliefIds.length === 0) {
      return;
    }

    try {
      const apiParams: GetSupportingBeliefCountFromBeliefsInputInterface = {
        beliefIds,
      };

      console.log('Fetching supporting belief counts for top user beliefs:', beliefIds);

      const response = await adminApi.getSupportingBeliefCountFromBeliefs(apiParams);

      if (response.success) {
        // Update top user belief counts
        setPowerUserBeliefCounts((prev) => {
          const updated = { ...prev };
          response.supportingBeliefCounts.forEach((count) => {
            updated[count.beliefId] = count.supportingBeliefCount;
          });
          return updated;
        });
        console.log(
          'Successfully fetched top user belief counts:',
          response.supportingBeliefCounts
        );
      } else {
        console.error('Failed to fetch top user belief counts:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching top user belief counts:', err);
    }
  };

  // Toggle top user beliefs expansion
  const togglePowerUserBeliefs = async (thoughtId: number): Promise<void> => {
    const newExpanded = new Set(expandedPowerUserBeliefs);

    if (newExpanded.has(thoughtId)) {
      newExpanded.delete(thoughtId);
    } else {
      newExpanded.add(thoughtId);
      // Fetch top user beliefs if not already loaded
      await fetchPowerUserBeliefs(thoughtId);
    }

    setExpandedPowerUserBeliefs(newExpanded);
  };

  // Load initial data
  useEffect(() => {
    if (feedId) {
      fetchFeed();
      fetchFeedContent();
      fetchPowerUsers(); // Fetch top users once at page level
    }
  }, [feedId]);

  // Close action menu when clicking outside or when modals open
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openActionMenu !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu-container')) {
          setOpenActionMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openActionMenu]);

  // Close action menu when modals open
  useEffect(() => {
    if (isSwitchModalOpen || isModalOpen) {
      setOpenActionMenu(null);
    }
  }, [isSwitchModalOpen, isModalOpen]);

  const toggleSupportingBeliefs = async (beliefId: number): Promise<void> => {
    const newExpanded = new Set(expandedBeliefs);

    if (newExpanded.has(beliefId)) {
      newExpanded.delete(beliefId);
    } else {
      newExpanded.add(beliefId);
      // Fetch supporting beliefs if not already loaded
      await fetchSupportingBeliefs(beliefId);
    }

    setExpandedBeliefs(newExpanded);
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const getBeliefPositionStyle = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get top user name by ID
  const getPowerUserName = (userId: number): string => {
    const powerUser = powerUsers.find((user) => user.account_id === userId);
    return powerUser?.name || `Top User ${userId}`;
  };

  // Modal handler functions
  const openBeliefModal = (belief: ClientBelief, author?: any): void => {
    setSelectedBelief(belief);
    setSelectedAuthor(author);
    setIsModalOpen(true);
  };

  const closeBeliefModal = (): void => {
    setIsModalOpen(false);
    setSelectedBelief(null);
    setSelectedAuthor(null);
  };

  // Action menu handlers
  const toggleActionMenu = (itemId: number): void => {
    setOpenActionMenu(openActionMenu === itemId ? null : itemId);
  };

  const handleSwitchAction = (item: SimulateFeedContent): void => {
    setContentToSwitch(item);
    setIsSwitchModalOpen(true);
    setOpenActionMenu(null);
  };

  const closeSwitchModal = (): void => {
    setIsSwitchModalOpen(false);
    setContentToSwitch(null);
  };

  const renderFeedItem = (item: SimulateFeedContent) => {
    const author = item.content.author;
    const content = item.content;
    const innerContent = content.content; // The actual belief/thought/feed data
    const isBelief = content.type === 'belief';
    const isThought = content.type === 'thought';
    const positionClass =
      isBelief && innerContent?.position ? getBeliefPositionStyle(innerContent.position) : null;

    // For beliefs, use the inner content ID (actual belief ID), otherwise use content ID
    const itemId = isBelief && innerContent ? innerContent.id || content.id : content.id;

    const isExpanded = expandedBeliefs.has(itemId);
    const supportingBeliefsForItem = supportingBeliefs[itemId] || [];
    const isLoadingSupporting = loadingSupportingBeliefs.has(itemId);

    // Get supporting belief count - show "loading..." if counts are being fetched
    let supportingBeliefCountText = '';
    if (isBelief) {
      if (loadingSupportingBeliefCounts) {
        supportingBeliefCountText = 'loading...';
      } else {
        const count = supportingBeliefCounts[itemId] || 0;
        supportingBeliefCountText = `${count}`;
      }
    }

    // Get thought text - for both beliefs and thoughts, it's in innerContent.text after enrichment
    const thoughtText = innerContent?.text;

    return (
      <div key={`${item.feed_rank}-${content.id}`} className="feed-item">
        <div className="feed-item-header">
          <div className="user-avatar">
            {author?.name ? getUserInitials(author.name) : <User size={20} />}
          </div>

          <div className="user-info">
            <div className="user-name-and-position">
              <span className="user-name">{author?.name || 'Anonymous User'}</span>
              {isBelief && positionClass && innerContent?.position && (
                <span className={`position-badge ${positionClass}`}>{innerContent.position}</span>
              )}
            </div>
            <div className="post-meta">
              <span className="rank">Rank #{item.feed_rank}</span>
              <span className="separator">•</span>
              <span className="content-type">{content.type}</span>
              {content.createdAt && (
                <>
                  <span className="separator">•</span>
                  <span className="date">{formatDate(content.createdAt)}</span>
                </>
              )}
            </div>
          </div>

          <div className="action-menu-container">
            <button
              className="action-menu-trigger"
              onClick={() => toggleActionMenu(itemId)}
              title="More actions"
            >
              <MoreHorizontal size={20} />
            </button>
            {openActionMenu === itemId && (
              <div className="action-menu">
                <button className="action-menu-item" onClick={() => handleSwitchAction(item)}>
                  Switch
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="feed-item-content">
          {/* Show thought text for both beliefs and thoughts */}
          {thoughtText && (
            <div className={`${isBelief ? 'belief-expand-container' : ''}`}>
              <div className="thought-text">{thoughtText}</div>
              {/* Add expand icon for beliefs */}
              {isBelief && (
                <button
                  className="belief-expand-icon"
                  onClick={() => {
                    // Create a ClientBelief object from the available data
                    const beliefData: ClientBelief = {
                      id: innerContent.id || 0,
                      content_id: content.id,
                      user_id: author?.account_id || 0,
                      thought_id: innerContent.thought_id || 0,
                      position: innerContent.position || 'unsure',
                      createdAt: content.createdAt || new Date(),
                      updatedAt: content.updatedAt || new Date(),
                      thought: {
                        content_id: content.id,
                        text: thoughtText,
                        createdAt: content.createdAt || new Date(),
                      },
                    };
                    openBeliefModal(beliefData, author);
                  }}
                  title="Explore belief deeper"
                >
                  <ArrowUpRight size={14} />
                </button>
              )}
            </div>
          )}

          {/* Show feed title if it exists */}
          {innerContent?.title && <div className="content-title">{innerContent.title}</div>}

          {/* Show feed description if it exists */}
          {innerContent?.description && (
            <div className="content-description">{innerContent.description}</div>
          )}
        </div>

        {isBelief && (
          <div className="supporting-beliefs-section">
            <button
              className="supporting-beliefs-toggle"
              onClick={() => toggleSupportingBeliefs(itemId)}
              disabled={isLoadingSupporting}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span>
                {isLoadingSupporting
                  ? 'Loading supporting beliefs...'
                  : `Supporting beliefs (${supportingBeliefCountText})`}
              </span>
            </button>

            {isExpanded && supportingBeliefsForItem.length > 0 && (
              <div className="supporting-beliefs-list">
                {supportingBeliefsForItem.slice(0, 5).map((supportingBelief) => {
                  // Get supporting belief count for this supporting belief
                  const isLoadingNestedCount = loadingNestedSupportingBeliefCounts.has(
                    supportingBelief.id
                  );
                  const nestedCount = nestedSupportingBeliefCounts[supportingBelief.id];
                  let nestedCountText = '';

                  if (isLoadingNestedCount) {
                    nestedCountText = ' (loading...)';
                  } else if (nestedCount !== undefined) {
                    nestedCountText = ` (${nestedCount})`;
                  }

                  return (
                    <div key={supportingBelief.id} className="supporting-belief-item">
                      <div
                        className={`supporting-belief-position position-badge ${getBeliefPositionStyle(
                          supportingBelief.position
                        )}`}
                      >
                        {supportingBelief.position}
                      </div>
                      <div className="supporting-belief-content">
                        <button
                          className="supporting-belief-text clickable"
                          onClick={() => openBeliefModal(supportingBelief, author)}
                        >
                          {supportingBelief.thought?.text || `Belief ${supportingBelief.id}`}
                        </button>
                        {nestedCountText && (
                          <span className="nested-supporting-count">{nestedCountText}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {supportingBeliefsForItem.length > 5 && (
                  <div className="supporting-beliefs-more">
                    +{supportingBeliefsForItem.length - 5} more supporting beliefs
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Top User Beliefs Section - Show for both beliefs and thoughts */}
        {thoughtText && (
          <div className="power-user-beliefs-section">
            <button
              className="power-user-beliefs-toggle"
              onClick={() => togglePowerUserBeliefs(innerContent?.thought_id || content.id)}
              disabled={loadingPowerUserBeliefs.has(innerContent?.thought_id || content.id)}
            >
              {expandedPowerUserBeliefs.has(innerContent?.thought_id || content.id) ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
              <span>
                {loadingPowerUserBeliefs.has(innerContent?.thought_id || content.id)
                  ? 'Loading top user beliefs...'
                  : 'Top User Beliefs'}
              </span>
            </button>

            {expandedPowerUserBeliefs.has(innerContent?.thought_id || content.id) && (
              <div className="power-user-beliefs-list">
                {(() => {
                  const thoughtId = innerContent?.thought_id || content.id;
                  const powerUserBeliefsForThought = powerUserBeliefs[thoughtId] || [];
                  const isLoadingPowerUser = loadingPowerUserBeliefs.has(thoughtId);

                  if (isLoadingPowerUser) {
                    return (
                      <div className="power-user-beliefs-loading">Loading top user beliefs...</div>
                    );
                  }

                  if (loadingPowerUsers) {
                    return (
                      <div className="power-user-beliefs-loading">Loading top user data...</div>
                    );
                  }

                  if (powerUserBeliefsForThought.length === 0) {
                    return (
                      <div className="power-user-beliefs-empty">
                        No top user beliefs found for this thought.
                      </div>
                    );
                  }

                  return powerUserBeliefsForThought
                    .filter((powerUserBelief) => {
                      const supportingCount = powerUserBeliefCounts[powerUserBelief.id] || 0;
                      return supportingCount > 0; // Only show users with supporting beliefs
                    })
                    .map((powerUserBelief) => {
                      const supportingCount = powerUserBeliefCounts[powerUserBelief.id] || 0;
                      const powerUserName = getPowerUserName(powerUserBelief.user_id);

                      return (
                        <div
                          key={powerUserBelief.id}
                          className="power-user-belief-item clickable"
                          onClick={() =>
                            openBeliefModal(powerUserBelief, {
                              account_id: powerUserBelief.user_id,
                              name: powerUserName,
                            })
                          }
                          title="Click to explore this belief"
                        >
                          <div className="power-user-belief-info">
                            <span className="power-user-name">{powerUserName}</span>
                            <div
                              className={`power-user-belief-position position-badge ${getBeliefPositionStyle(
                                powerUserBelief.position
                              )}`}
                            >
                              {powerUserBelief.position}
                            </div>
                          </div>
                          <div className="power-user-supporting-count">({supportingCount})</div>
                        </div>
                      );
                    });
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="feed-simulation-page">
        <div className="loading">Loading feed simulation...</div>
      </div>
    );
  }

  if (error && !feed) {
    return (
      <div className="feed-simulation-page">
        <div className="error-message">
          <p>Error: {error}</p>
          <button className="btn-secondary" onClick={() => navigate(`/feeds/${feedId}`)}>
            Back to Feed Details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-simulation-page">
      {/* Header */}
      <div className="simulation-header">
        <div className="header-top">
          <button className="btn-secondary" onClick={() => navigate(`/feeds/${feedId}`)}>
            <ArrowLeft size={16} />
            Back to Feed Details
          </button>
          <div className="feed-info">
            <Rss size={24} className="feed-icon" />
            <div className="feed-meta">
              <span className="feed-title">{feed?.title || 'Untitled Feed'}</span>
              <span className="feed-subtitle">Feed Simulation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feed Content */}
      <div className="feed-simulation-content">
        {feedContent.length === 0 ? (
          <div className="empty-state">
            <Rss size={48} />
            <h4>No Content to Display</h4>
            <p>This feed doesn't have any content items to simulate.</p>
          </div>
        ) : (
          <div className="feed-items">{feedContent.map(renderFeedItem)}</div>
        )}
      </div>

      {/* Recursive Belief Modal */}
      {selectedBelief && (
        <RecursiveBeliefModal
          isOpen={isModalOpen}
          onClose={closeBeliefModal}
          initialBelief={selectedBelief}
          initialAuthor={selectedAuthor}
        />
      )}

      {/* Switch Content Modal */}
      <SwitchContentModal
        isOpen={isSwitchModalOpen}
        onClose={closeSwitchModal}
        contentToSwitch={contentToSwitch}
      />
    </div>
  );
};

export default FeedSimulation;
